import React, { ReactElement, useEffect, useState } from 'react';
import {
  View, StyleSheet, Text, TextInput, Pressable, ActivityIndicator, Platform,
} from 'react-native';
import { getStateByCodeAndCountry, getStatesOfCountry } from 'country-state-city/src/state';
import { getCountryByCode } from 'country-state-city/src/country';
import Modal from 'react-native-modal';
import { getCitiesOfCountry, getCitiesOfState } from 'country-state-city/src/city';
import Colors from '../../../../constants/Colors';
import Style from '../../../../constants/Style';
import {
  BUILDING_SETTINGS_PRIORITIES, BuildingSettingsPriority, DAYS, Day,
} from '../BuildingSettingsModal/data';
import CountrySelector, { Country } from './components/CountrySelector';
import CitySelector, { City } from './components/CitySelector';
import StateSelector, { State } from './components/StateSelector';
import BuildingLayout from './components/BuildingLayout';
import {
  BUILDING_NAME_EMPTY_ERROR, BUILDING_NAME_EXISTS_ERROR, BUILDING_NAME_MAX_LENGTH_ERROR,
  CITY_EMPTY_ERROR, COUNTRY_EMPTY_ERROR, STATE_EMPTY_ERROR,
} from './components/errorMessages';
import updateBuilding from '../../../../requests/Locations/Buildings/UpdateBuilding';
import nameRegex, { externalIdRegex, integerRegex } from '../../../../components/Regex';
import asNumber from '../../../../components/AsNumber';
import titleCase from '../../../../components/TitleCase';
import PressableStyled from '../../../../components/PressableStyled';
import Button from '../../../../components/Button';
import CloseModalIcon from '../../../../components/CloseModalIcon';
import AlertModal from '../../../../components/AlertModal';

export type Building = {
  Name: string,
  ExternalId?: string,
  LocationId: string,
  ParentId: string,
  CityName: string,
  CountryCode: string,
  StateCode: string,
  floors: object,
  Floor?: { IsAssetAvailable: boolean },
  Area?: { IsAssetAvailable: boolean },
  Space?: { IsAssetAvailable: boolean },
  SlaHours?: BuildingSettingsPriority[],
  WorkingHours?: Day[],
  LocationCapacity?: number,
}

interface AddBuildingModalProps {
  addBuildingModalShown: boolean,
  hideAddBuildingModal: () => void,
  showScreenAlert,
  building?: Building,
  addBuilding?,
  editBuilding?,
  ParentId?: string,
  checkIfNameUnique,
}

const AddBuildingModal = ({
  addBuildingModalShown, hideAddBuildingModal, building, addBuilding, editBuilding, showScreenAlert,
  ParentId, checkIfNameUnique,
}: AddBuildingModalProps) => {
  const [visibilityToggleEnabled, setVisibilityToggleEnabled] = useState<boolean>(true);
  const [name, setName] = useState<string>(building?.Name ?? '');
  const [capacity, setCapacity] = useState<string | null>(null);
  const [externalId, setExternalId] = useState<string | null>(building?.ExternalId ?? null);
  const [selectedCountry, setSelectedCountry] = useState<Country>(
    building?.CountryCode ? getCountryByCode(building.CountryCode) ?? null : null,
  );
  const [selectedState, setSelectedState] = useState<State>(
    building?.StateCode ? getStateByCodeAndCountry(building.StateCode, building.CountryCode)
      ?? null : null,
  );
  const [selectedCity, setSelectedCity] = useState<City>(
    (building?.CityName && (building?.CountryCode || building.StateCode))
      ? (getCitiesOfCountry(building?.CountryCode)
        ?? getCitiesOfState('US', building?.StateCode))
        ?.filter((city: City) => city.name.toLowerCase() === building.CityName?.toLowerCase())[0]
      ?? null : null,
  );
  const [areasCanContainAssets, setAreasCanContainAssets] = useState<boolean>(
    building?.Area?.IsAssetAvailable ?? true,
  );
  const [spacesCanContainAssets, setSpacesCanContainAssets] = useState<boolean>(
    building?.Space?.IsAssetAvailable ?? true,
  );
  const [alertTitle, setAlertTitle] = useState<string>('');
  const [alertMessage, setAlertMessage] = useState<string | ReactElement>(null);
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertAction, setAlertAction] = useState<() => void>(null);
  const [alertActionTitle, setAlertActionTitle] = useState<string | null>(null);
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [capacityError,
    setCapacityError] = useState<string | null>(building?.LocationCapacity?.toString());
  const [externalIdError, setExternalIdError] = useState<string | null>(null);
  const [countryInvalid, setCountryInvalid] = useState<boolean>(false);
  const [stateInvalid, setStateInvalid] = useState<boolean>(false);
  const [cityInvalid, setCityInvalid] = useState<boolean>(false);
  const [countryDropdownShown, setCountryDropdownShown] = useState<boolean>(false);
  const [stateDropdownShown, setStateDropdownShown] = useState<boolean>(false);
  const [cityDropdownShown, setCityDropdownShown] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);

  const restoreDefaults = () => {
    closeDropdowns();
    setName(building?.Name ?? '');
    setCapacity(building?.LocationCapacity?.toString());
    setExternalId(building?.ExternalId);
    setSelectedCountry(building ? getCountryByCode(building?.CountryCode) ?? null : null);
    setSelectedState(building
      ? getStateByCodeAndCountry(building.StateCode, building.CountryCode) ?? null : null);
    setSelectedCity(
      (building?.CityName && (building?.CountryCode || building.StateCode))
        ? (getCitiesOfCountry(building?.CountryCode)
          ?? getCitiesOfState('US', building?.StateCode))
          ?.filter((city: City) => city.name.toLowerCase() === building.CityName?.toLowerCase())[0]
        ?? null : null,
    );
    setNameError(null);
    setCapacityError(null);
    setExternalIdError(null);
    setCountryInvalid(false);
    setStateInvalid(false);
    setCityInvalid(false);
    setAreasCanContainAssets(building?.Area?.IsAssetAvailable ?? true);
    setSpacesCanContainAssets(building?.Space?.IsAssetAvailable ?? true);
  };

  const editName = (text: string) => {
    setName(text);
    if (!text.trim().length) setNameError(BUILDING_NAME_EMPTY_ERROR);
    else if (!(nameRegex.exp.test(text.trim()))) setNameError(`Building name${nameRegex.message}`);
    else if (text.trim().length > 50) setNameError(BUILDING_NAME_MAX_LENGTH_ERROR);
    else if (!checkIfNameUnique(text.trim(), building?.LocationId)) {
      setNameError(BUILDING_NAME_EXISTS_ERROR);
    } else setNameError(null);
  };

  const editCapacity = (text: string) => {
    setCapacity(text);
    if (text?.trim().length > 0 && !integerRegex.exp.test(text.trim())) {
      setCapacityError(externalIdRegex.message);
    } else setCapacityError(null);
  };

  const editExternalId = (text: string) => {
    setExternalId(text);
    if (text?.trim().length > 0 && !externalIdRegex.exp.test(text.trim())) {
      setExternalIdError(externalIdRegex.message);
    } else setExternalIdError(null);
  };

  useEffect(() => {
    if (selectedCountry?.isoCode !== selectedState?.countryCode) setSelectedState(null);
    if (selectedCountry?.isoCode !== selectedCity?.countryCode) setSelectedCity(null);
    setCountryInvalid(false);
  }, [selectedCountry]);

  useEffect(() => {
    if (selectedState?.isoCode !== selectedCity?.stateCode) setSelectedCity(null);
    setStateInvalid(false);
  }, [selectedState]);

  useEffect(() => setCityInvalid(false), [selectedCity]);

  useEffect(() => {
    if (addBuildingModalShown) {
      setVisibilityToggleEnabled(false);
      const timeout = setTimeout(() => {
        setVisibilityToggleEnabled(true);
        clearTimeout(timeout);
      }, 500);
    }
  }, [addBuildingModalShown]);

  const closeModal = visibilityToggleEnabled ? hideAddBuildingModal : null;

  const showAlert = (
    title: string,
    message: string | ReactElement,
    error: boolean = true,
    action: () => void = null,
    actionTitle: string = null,
  ) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertError(error);
    setAlertAction(() => action);
    setAlertActionTitle(actionTitle);
    setLoading(false);
    setAlertVisible(true);
  };

  const updateBuildingError = (errorText?: string) => showAlert(
    'Error',
    errorText
    ?? `We are unable to ${building ? 'edit' : 'add'} the building right now. Please try again later.`,
  );

  const statesShown: boolean = selectedCountry?.isoCode === 'US';

  const citiesShown: boolean = selectedCountry && (!statesShown || !!selectedState);

  const validate = () => {
    closeDropdowns();
    const errorMessages: string[] = [];
    if (!name.trim().length) {
      errorMessages.push(`- ${BUILDING_NAME_EMPTY_ERROR}`);
      setNameError(BUILDING_NAME_EMPTY_ERROR);
    }
    if (!(nameRegex.exp.test(name.trim()))) {
      errorMessages.push(`- Building name${nameRegex.message}`);
      setNameError(`Building name${nameRegex.message}`);
    }
    if (name.trim().length > 50) {
      errorMessages.push(`- ${BUILDING_NAME_MAX_LENGTH_ERROR}`);
      setNameError(BUILDING_NAME_MAX_LENGTH_ERROR);
    }
    if (!checkIfNameUnique(name.trim(), building?.LocationId)) {
      errorMessages.push(`- ${BUILDING_NAME_EXISTS_ERROR}`);
      setNameError(BUILDING_NAME_EXISTS_ERROR);
    }
    if (capacity?.trim().length > 0 && !integerRegex.exp.test(capacity?.trim())) {
      errorMessages.push(`- Capacity${integerRegex.message}`);
      setCapacityError(`Capacity${integerRegex.message}`);
    }
    if (externalId?.trim().length > 0 && !externalIdRegex.exp.test(externalId?.trim())) {
      errorMessages.push(`- ${externalIdRegex.message}`);
      setExternalIdError(externalIdRegex.message);
    }
    if (!selectedCountry) {
      errorMessages.push(`- ${COUNTRY_EMPTY_ERROR}`);
      setCountryInvalid(true);
    }
    if (statesShown && !selectedState) {
      errorMessages.push(`- ${STATE_EMPTY_ERROR} for ${selectedCountry?.name}`);
      setStateInvalid(true);
    }
    if ((getCitiesOfCountry(selectedCountry?.isoCode)?.length > 0
      || getCitiesOfState(selectedState?.countryCode, selectedState?.isoCode)?.length > 0)
      && !selectedCity) {
      errorMessages.push(`- ${CITY_EMPTY_ERROR} for ${selectedState?.name ?? selectedCountry?.name}`);
      setCityInvalid(true);
    }
    if (errorMessages.length === 0) {
      setLoading(true);
      updateBuilding(
        name.trim(),
        true,
        areasCanContainAssets,
        spacesCanContainAssets,
        selectedCity.countryCode,
        selectedCity.stateCode,
        selectedCity.name,
        building?.SlaHours ?? BUILDING_SETTINGS_PRIORITIES,
        building?.WorkingHours ?? DAYS,
        building?.LocationId,
        externalId?.trim(),
        asNumber(capacity),
      ).then((res) => {
        if (res && !res.isError) {
          const newBuilding: Building = {
            ...building,
            floors: building?.floors ?? [],
            Name: name.trim(),
            ParentId,
            CountryCode: selectedCity.countryCode,
            StateCode: selectedCity.stateCode,
            CityName: titleCase(selectedCity?.name),
            LocationId: building?.LocationId ?? res.LocationId,
            Floor: { IsAssetAvailable: true },
            Area: { IsAssetAvailable: areasCanContainAssets },
            Space: { IsAssetAvailable: spacesCanContainAssets },
            ExternalId: externalId?.trim(),
            SlaHours: building?.SlaHours ?? BUILDING_SETTINGS_PRIORITIES,
            WorkingHours: building?.WorkingHours ?? DAYS,
            LocationCapacity: asNumber(capacity),
          };
          if (building) editBuilding(newBuilding);
          else addBuilding(newBuilding);
          setLoading(false);
          hideAddBuildingModal();
          showScreenAlert(
            `Building ${building ? 'edited' : 'added'} successfully`,
            <Text>
              <Text style={Style.alertBoldText}>{name}</Text>
              {` was successfully ${building ? 'edited' : 'added'}`}
            </Text>,
            false,
          );
        } else updateBuildingError(res?.errorText);
      }).catch((error) => updateBuildingError(error?.message));
    } else showAlert('Error', errorMessages.join('\n'));
  };

  const closeDropdowns = () => {
    setCountryDropdownShown(false);
    setStateDropdownShown(false);
    setCityDropdownShown(false);
  };

  return <Modal
    isVisible={addBuildingModalShown}
    onBackButtonPress={hideAddBuildingModal}
    onSwipeComplete={closeModal}
    onBackdropPress={closeModal}
    style={Style.modal}
    onModalHide={restoreDefaults}
    onModalWillShow={restoreDefaults}
  >
    <Pressable onPress={closeDropdowns}>
      <View style={[Style.modalContent, styles.container]}>
        <Text style={styles.title}>
          {`${building ? 'Edit' : 'Add'} building`}
        </Text>
        <Text style={styles.description}>{'Fields marked with * are mandatory'}</Text>
        <View style={styles.content}>
          <View style={styles.textFieldSection}>
            <Text style={styles.subTitle}>{'Name and floors'}</Text>
            <Text style={styles.description}>{'Name *'}</Text>
            <TextInput
              maxLength={50}
              value={name}
              onChangeText={editName}
              style={[styles.description, styles.input, nameError?.length > 0 && styles.invalid]}
              placeholder='Enter'
              placeholderTextColor={styles.description.color}
            />
            {nameError?.length > 0 && <Text style={styles.errorText}>{nameError}</Text>}
            <Text style={[styles.description, styles.label]}>{'Country *'}</Text>
            <CountrySelector {...{
              selectedCountry,
              setSelectedCountry,
              countryInvalid,
              countryDropdownShown,
              setCountryDropdownShown,
            }} />
            {statesShown && <>
              <Text style={[styles.description, styles.label]}>{'State *'}</Text>
              <StateSelector
                {...{
                  selectedState,
                  setSelectedState,
                  stateInvalid,
                  stateDropdownShown,
                  setStateDropdownShown,
                }}
                states={getStatesOfCountry(selectedCountry?.isoCode)}
              />
            </>}
            {citiesShown && <>
              <Text style={[styles.description, styles.label]}>{'City *'}</Text>
              <CitySelector
                cities={getCitiesOfState(selectedState?.countryCode, selectedState?.isoCode)
                  ?.length > 0
                  ? getCitiesOfState(selectedState?.countryCode, selectedState?.isoCode)
                  : getCitiesOfCountry(selectedCountry?.isoCode)}
                {...{
                  selectedCity,
                  setSelectedCity,
                  cityInvalid,
                  cityDropdownShown,
                  setCityDropdownShown,
                }}
              />
            </>}
            <Text style={[styles.description, styles.label]}>{'Capacity'}</Text>
            <TextInput
              maxLength={5}
              value={capacity}
              onChangeText={editCapacity}
              style={[
                styles.description, styles.input, externalIdError?.length > 0 && styles.invalid,
              ]}
              placeholder='Enter'
              placeholderTextColor={styles.description.color}
            />
            {capacityError?.length > 0
              && <Text style={styles.errorText}>{capacityError}</Text>}
            <Text style={[styles.description, styles.label]}>{'External ID'}</Text>
            <TextInput
              maxLength={50}
              value={externalId}
              onChangeText={editExternalId}
              style={[
                styles.description, styles.input, externalIdError?.length > 0 && styles.invalid,
              ]}
              placeholder='Enter'
              placeholderTextColor={styles.description.color}
            />
            {externalIdError?.length > 0
              && <Text style={styles.errorText}>{externalIdError}</Text>}
          </View>
          <BuildingLayout {...{
            areasCanContainAssets,
            spacesCanContainAssets,
            setAreasCanContainAssets,
            setSpacesCanContainAssets,
          }} />
        </View>
        <View style={styles.buttonContainer}>
          <Button
            title='Cancel'
            onPress={hideAddBuildingModal}
          />
          <Button
            affirmative
            title={building ? 'Save' : 'Add'}
            onPress={validate}
            testID='lm-add-building-modal-btn'
            disabled={!!nameError || !!externalIdError || !!capacityError}
          />
        </View>
        <PressableStyled
          style={Style.closeButton}
          onPress={hideAddBuildingModal}
        >
          <CloseModalIcon />
        </PressableStyled>
      </View>
    </Pressable>
    <ActivityIndicator
      size='large'
      style={Style.activityIndicator}
      animating={loading}
    />
    <AlertModal
      onModalHide={alertError ? undefined : hideAddBuildingModal}
      title={alertTitle}
      message={alertMessage}
      error={alertError}
      modalVisible={alertVisible}
      setModalVisible={setAlertVisible}
      action={alertAction}
      actionTitle={alertActionTitle}
    />
  </Modal>;
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFF',
    width: '70%',
    padding: 40,
    paddingTop: 60,
    alignItems: 'center',
    borderRadius: 10,
    alignSelf: 'center',
    maxWidth: 1012,
    height: 840,
  },
  title: {
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Poppins_600SemiBold',
    fontSize: 28,
    lineHeight: 35,
    marginBottom: 12,
    textAlign: 'center',
  },
  description: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  subTitle: {
    fontSize: 20,
    lineHeight: 30,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack600,
    marginBottom: 27,
  },
  label: {
    marginTop: 12,
  },
  input: {
    padding: 10,
    lineHeight: Platform.OS === 'ios' ? undefined : 24,
    borderRadius: 5,
    backgroundColor: Colors.light.hereworksWhite200,
    marginTop: 4,
    alignSelf: 'stretch',
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
  },
  content: {
    flex: 1,
    flexDirection: 'row',
    marginTop: 32,
    marginBottom: 28,
    alignSelf: 'stretch',
  },
  buttonContainer: {
    flexDirection: 'row',
  },
  textFieldSection: {
    flex: 0.5,
    alignItems: 'flex-start',
    marginEnd: 32,
  },
  invalid: {
    borderColor: Colors.light.hereworksRed500,
    borderWidth: 1,
  },
  errorText: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksRed500,
  },
});

export default AddBuildingModal;
